// SourceColumnSetManage.jsx

import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import AIPanel from "../../../components/AIChat/AIPanel";
import { Loading } from "spark-core-dx/components";
import { useCoreStateContext } from "spark-core-dx/contexts";
import { useCoreRoute, useGenericManager } from "spark-core-dx/hooks";
import { ItemServiceFactory } from "../../../services/ItemServiceFactory";
import ColumnSetEditor from "../../../components/ColumnSetEditor";

const SourceColumnSetManage = () => {
  const itemService = new ItemServiceFactory();
  const { coreNavigate } = useCoreRoute();
  const coreState = useCoreStateContext();
  const pageRef = useRef(null);

  // --- Managers ---
  const sourceObjectMgr = useGenericManager(itemService.ItemServiceEnum.SourceObject);
  const chatSessionMgr = useGenericManager(itemService.ItemServiceEnum.AIChatSession);

  // --- State ---
  const [sourceObject, setSourceObject] = useState(null);
  const [currentSourceObject, setCurrentSourceObject] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [refresh, setRefresh] = useState("0");
  const [session, setSession] = useState(null);
  const [metadata,setMetadata] = useState(null)

  const { id } = useParams(); // assumed to be SourceObjectId

  // --- guard to avoid feedback loop on onChange ---
  const lastPayloadStrRef = useRef("");

  
  const loadMetadata = async () => {
    if (metadata) return;
    const resp = await sourceObjectMgr.GetMetadata({});
    const items = resp?.Items ?? [];
    console.log("ALL THE METATDATR",items);
    setMetadata(items);
  };

  const setCurrentSourceObjectIfChanged = useCallback((next) => {
    // Avoid exceptions from circulars; if it fails, fall back to reference compare
    let nextStr = "";
    try {
      nextStr = JSON.stringify(next);
    } catch {
      /* ignore */
    }

    if (nextStr && nextStr === lastPayloadStrRef.current) return;

    setCurrentSourceObject((prev) => {
      try {
        const prevStr = JSON.stringify(prev);
        if (prevStr === nextStr) return prev; // no structural change
      } catch {
        if (prev === next) return prev; // fallback to ref compare
      }
      lastPayloadStrRef.current = nextStr || "";
      return next;
    });
  }, []);

  useEffect(() => {
    const getChatSession = async (referenceId) => {
      const chatSessionResult = await chatSessionMgr.Query({
        model:
          "x => x.ReferenceId == referenceId && x.IsDeleted == false && x.IsActive == true",
        params: { referenceId },
      });

      if (chatSessionResult?.Success && chatSessionResult?.Items?.length > 0) {
        setSession(chatSessionResult.Items.first());
      }
    };

    const getSourceObjectData = async () => {
      const redirectData = coreState.get("InternalRedirectData");

      // If we were navigated here with a preloaded SourceObject in coreState
      if (redirectData && redirectData.SourceObjectId) {
        if (id === redirectData._id || id === redirectData.SourceObjectId) {
          setSourceObject(redirectData);
          setCurrentSourceObject(redirectData);
          await getChatSession(redirectData._id ?? redirectData.SourceObjectId);
          return;
        }
      }

      // Otherwise, fetch by id
      if (id) {
        const r = await sourceObjectMgr.getItem(id);
        if (r?.Success && r.Items?.length > 0) {
          const so = r.Items.first();
          if (!session) {
            await getChatSession(so._id ?? so.SourceObjectId);
          }
          setSourceObject(so);
          setCurrentSourceObject(so);
        }
      }
    };

    if (coreState && !sourceObject) {
      getSourceObjectData().then(() => loadMetadata().then(()=> setIsLoading(false)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally once

  const onUpdatePanel = async (UICommand, item, value) => {
    // When AI panel returns an updated payload, keep our working copy in sync
    if (UICommand === "update" && item?.Fields) {
      setCurrentSourceObjectIfChanged(item);
      setRefresh(item); // ColumnSetEditor can use this to resync
    }
  };

  const handleSaveSourceObject = async (updatedSourceObject) => {
    const result = await sourceObjectMgr.saveItem(updatedSourceObject);
    if (result?.Success) {
      setRefresh(String(Date.now()));
      const saved = result.Items.first();
      setSourceObject(saved);
      // also advance the "current" model so editor doesn't bounce back
      setCurrentSourceObjectIfChanged(saved);
    }
    return result;
  };

  const handleCurrentSourceObject = (value) => {
    // Avoid deep-cloning every time; only update if structurally different.
    setCurrentSourceObjectIfChanged(value);
  };

  const startChatPrompt = null; //ignore startup promt

      
  return (
    <>
      {!isLoading ? (
        <AIPanel
          childrenRef={pageRef}
          open={true}
          onUpdatePanel={onUpdatePanel}
          mapToClass={"core-layout"}
          params={{ id }}
          session={session}
          payload={currentSourceObject}
          referenceId={id}
          enableContentDragHiding={false}
          minLeft={825}
          minRight={400}
          startChat={startChatPrompt}
          // TODO: Update these two strings to match your orchestration setup
          referenceName={"SourceObject"}
          operationName={null}//"AISourceColumnSet"}
          splashMessage={
            "Ready to tune your column set? Tell me which fields should be visible, and I’ll help configure them."
          }
          introMessage = "Manage your columns!"
          onCancel={() => {
            coreNavigate("/dynamic/sourceobject/");
          }}
        >
          <div className="column-set-editor">
            <ColumnSetEditor
              sourceObject={sourceObject}
              fields={sourceObject?.Fields ?? []}
              fieldItemMetadata={metadata?.find(x=>x.Name === "SourceObjectField")}
              onSave={handleSaveSourceObject}
              onChange={handleCurrentSourceObject}
              refresh={refresh}
            />
          </div>
        </AIPanel>
      ) : (
        <div className="centered-loading">
          <Loading />
        </div>
      )}
    </>
  );
};

export default SourceColumnSetManage;
